import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

interface MdLinkProps {
  path: string;
  label?: string;
  className?: string;
  showIcon?: boolean;
}

/**
 * Resolve a relative or shorthand path to a full VPS absolute path.
 */
function resolveVpsPath(path: string): string {
  // Already absolute VPS path
  if (path.startsWith('/opt/union-threads/') || path.startsWith('/var/lib/docker/volumes/')) {
    return path;
  }
  // SecondBrain workspace shorthand
  if (path.startsWith('workspace/')) {
    return '/var/lib/docker/volumes/ut-secondbrain_workspace-data/_data/' + path.replace('workspace/', '');
  }
  // Relative path — assume relative to union-threads root
  return '/opt/union-threads/' + path.replace(/^\.\//, '');
}

/**
 * Clickable .md link that navigates to the Docs viewer.
 * Works from any page in the app.
 */
export function MdLink({ path, label, className, showIcon = true }: MdLinkProps) {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const resolvedPath = resolveVpsPath(path);
    navigate(`/docs?path=${encodeURIComponent(resolvedPath)}`);
  };

  const displayLabel = label || path.split('/').pop() || path;

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1 text-accent-purple-light hover:underline cursor-pointer ${className || ''}`}
      title={`Open in Docs: ${path}`}
    >
      <span className="truncate">{displayLabel}</span>
      {showIcon && <ExternalLink className="h-3 w-3 flex-shrink-0" />}
    </button>
  );
}

export default MdLink;
